import { Box, Button, Container, Typography } from "@mui/material";
import { useState, memo, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import MultiSelectField from "./components/MultiSelect";
import CheckboxField from "./components/CheckBoxField";
import NumberField from "./components/NumberField";
import SelectField from "./components/SelectField";
import TextareaField from "./components/TextAreaField";
import {
  INPUT_TYPES,
  LOGICAL_OPERATORS,
  OPERATORS,
} from "constants/form-builder";
import SliderField from "./components/SliderField";
import UrlField from "./components/UrlField";
import TextFieldComponent from "./components/TextInput";
import EmailField from "./components/EmailField";
import PhoneField from "./components/PhoneField";
import RadioGroupField from "./components/RadioGroup";
import FileUploadField from "./components/FileUpload";
import ImageUploadField from "./components/ImageFileUpload";
import DatePickerField from "./components/DateInput";
import { ReactComponent as Logo } from "../../images/valoo-logo.svg";
import { STORAGE_KEYS } from "constants/common";
import FieldInfo from "./field-info";
import PictureSelectField from "./components/picture-select-field";
import MultiSelectChip from "./components/MultiSelectChip";
import FormInfoText from "./components/FomrInfoText";
import FormInfoImage from "./components/FormInfoImage";
import CheckboxGroup from "./components/CheckboxGroup";

const SurveyFormRender = ({
  formSchema,
  userType,
  formSubmitHandler = () => {},
  handleNextStep = async () => {},
  defaultValues = {},
  stepCount,
  isPreviewMode = false,
}) => {
  const form = useForm({
    defaultValues,
  });

  const { handleSubmit, watch, setValue } = form;
  const [currentStep, setCurrentStep] = useState(0);

  // Use watch() to get real-time values
  const watchedValues = watch();

  // Utility to check field visibility based on dependencies
  const isFieldVisible = (field) => {
    // const visibleToUser =
    //   userType === "admin" ? true : field.visibleUserTypes.includes(userType);
    if ([INPUT_TYPES.RICHTEXT, INPUT_TYPES.INFO_IMAGE].includes(field.type))
      return true;
    if (!field.dependencies) return true;
    return evaluateDependency(field.dependencies, watchedValues);
  };

  // Utility to check step visibility
  const isStepVisible = (step) => {
    if (!step.dependencies) return true;
    return evaluateDependency(step.dependencies, watchedValues);
  };

  // Evaluate dependencies in real-time
  const evaluateDependency = (dependency, values) => {
    const { operator, rules } = dependency;
    if (!rules || rules.length === 0) return true;

    const results = rules.map((rule) => {
      if (rule.rules) {
        return evaluateDependency(rule, values);
      } else {
        const fieldValue = values[rule.field];

        // Helper function to safely format value based on input type
        const formatValue = (value, inputType) => {
          if (value === null || value === undefined) return null;

          // Handle array types (multiselect)
          if (Array.isArray(value)) {
            return value.filter((item) => item !== null && item !== undefined);
          }

          // Handle number types
          if (
            inputType === INPUT_TYPES.NUMBER ||
            inputType === INPUT_TYPES.SLIDER
          ) {
            const numValue = Number(value);
            return isNaN(numValue) ? null : numValue;
          }

          // Handle string types
          if (typeof value === "string") {
            return value.trim() === "" ? null : value;
          }

          return value;
        };

        // Format field value based on field type
        const formattedFieldValue = formatValue(fieldValue, rule.fieldType);
        const formattedRuleValue = formatValue(rule.value, rule.fieldType);

        switch (rule.operator) {
          case OPERATORS.EQUAL:
          case OPERATORS.EQUALS:
            return formattedFieldValue === formattedRuleValue;
          case OPERATORS.NOT_EQUAL:
            return formattedFieldValue !== formattedRuleValue;
          case OPERATORS.GREATER:
            if (formattedFieldValue === null || formattedRuleValue === null)
              return false;
            return Number(formattedFieldValue) > Number(formattedRuleValue);
          case OPERATORS.LESSER:
            if (formattedFieldValue === null || formattedRuleValue === null)
              return false;
            return Number(formattedFieldValue) < Number(formattedRuleValue);
          case OPERATORS.GREATER_EQUAL:
            if (formattedFieldValue === null || formattedRuleValue === null)
              return false;
            return Number(formattedFieldValue) >= Number(formattedRuleValue);
          case OPERATORS.LESSER_EQUAL:
            if (formattedFieldValue === null || formattedRuleValue === null)
              return false;
            return Number(formattedFieldValue) <= Number(formattedRuleValue);
          case OPERATORS.CONTAINS:
            if (formattedFieldValue === null) return false;
            if (Array.isArray(formattedFieldValue)) {
              return formattedFieldValue.includes(formattedRuleValue);
            }
            return String(formattedFieldValue).includes(
              String(formattedRuleValue || "")
            );
          case OPERATORS.IS_EMPTY:
            if (formattedFieldValue === null) return true;
            if (Array.isArray(formattedFieldValue)) {
              return formattedFieldValue.length === 0;
            }
            return String(formattedFieldValue).trim() === "";
          case OPERATORS.IS_NOT_EMPTY:
            if (formattedFieldValue === null) return false;
            if (Array.isArray(formattedFieldValue)) {
              return formattedFieldValue.length > 0;
            }
            return String(formattedFieldValue).trim() !== "";
          default:
            return false;
        }
      }
    });

    return operator === LOGICAL_OPERATORS.AND
      ? results.every(Boolean)
      : results.some(Boolean);
  };

  useEffect(() => {
    if (!isPreviewMode) {
      const savedData = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.FORM_DATA)
      );
      const activeStep = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.ACTIVE_STEP)
      );
      if (activeStep) {
        setCurrentStep(activeStep);
      }
      if (savedData) {
        Object.keys(savedData).forEach((key) => {
          setValue(key, savedData[key]);
        });
      }
    }
  }, [setValue, isPreviewMode]);

  // Watch all fields and save to localStorage
  useEffect(() => {
    const subscription = watch((data) => {
      localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(data));
      localStorage.setItem(
        STORAGE_KEYS.ACTIVE_STEP,
        JSON.stringify(currentStep)
      );
    });
    return () => subscription.unsubscribe();
  }, [watch, currentStep]);

  useEffect(() => {
    if (stepCount) {
      setCurrentStep(stepCount);
    }
  }, [stepCount]);

  // Handle form submission
  const onSubmit = (data) => {
    formSubmitHandler(data);
    localStorage.removeItem("formData");
    localStorage.removeItem("activeStep");
  };

  const renderField = (field) => {
    switch (field.type) {
      case INPUT_TYPES.MULTISELECT:
        return <MultiSelectField field={field} />;
      case INPUT_TYPES.CHECKBOX:
        return <CheckboxField field={field} />;
      case INPUT_TYPES.NUMBER:
        return <NumberField field={field} />;
      case INPUT_TYPES.SELECT:
        return <SelectField field={field} />;
      case INPUT_TYPES.TEXTAREA:
        return <TextareaField field={field} />;
      case INPUT_TYPES.IMAGEUPLOAD:
        return <ImageUploadField field={field} />;
      case INPUT_TYPES.SLIDER:
        return <SliderField field={field} />;
      case INPUT_TYPES.DATEPICKER:
        return <DatePickerField field={field} />;
      case INPUT_TYPES.URL:
        return <UrlField field={field} />;
      case INPUT_TYPES.TEXTFIELD:
        return <TextFieldComponent field={field} />;
      case INPUT_TYPES.EMAIL:
        return <EmailField field={field} />;
      case INPUT_TYPES.PHONE:
        return <PhoneField field={field} />;
      case INPUT_TYPES.RADIOGROUP:
        return <RadioGroupField field={field} />;
      case INPUT_TYPES.FILEUPLOAD:
        return <FileUploadField field={field} />;
      case INPUT_TYPES.ICONRADIO:
        return <PictureSelectField field={field} />;
      case INPUT_TYPES.MULTICHIP:
        return <MultiSelectChip field={field} />;
      case INPUT_TYPES.RICHTEXT:
        return <FormInfoText field={field} />;
      case INPUT_TYPES.INFO_IMAGE:
        return <FormInfoImage field={field} />;
      case INPUT_TYPES.CHECKBOXGROUP:
        return <CheckboxGroup field={field} />;
      default:
        return <div>Unsupported field type: {field.type}</div>;
    }
  };

  const handleNextStepClick = async () => {
    try {
      const currentFields = formSchema.steps[currentStep].fields;
      const currentFieldsName = currentFields.map((field) => field.id);
      const isStepValid = await form.trigger(currentFieldsName);
      if (isStepValid) {
        const payload = {};
        currentFieldsName.forEach((name) => {
          payload[name] = form.getValues(name);
        });
        await handleNextStep(payload, {
          stepCount: currentStep,
          isFinalStep: currentStep === formSchema.steps.length - 1,
        });
        setCurrentStep((prevStep) => prevStep + 1);
      }
    } catch (error) {}
  };

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          px: 1,
          maxWidth: "xs",
          mx: "auto",
          my: 8,
        }}
      >
        <Box
          sx={{
            mb: {
              xs: 2,
              sm: 3,
              md: 6,
            },
            textAlign: "center",
          }}
        >
          <Logo alt="Valoo Logo" />
        </Box>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {formSchema.steps.map((step, index) => {
              if (index !== currentStep || !isStepVisible(step)) return null;

              return (
                <div key={step.id}>
                  <Typography
                    sx={{
                      textAlign: "center",
                      mb: 1,
                    }}
                    variant="h5"
                  >
                    {step.title}
                  </Typography>

                  <Typography
                    sx={{
                      textAlign: "center",
                      marginBottom: "20px",
                      fontWeight: 400,
                      mb: 4,
                    }}
                    variant="body1"
                  >
                    {step.caption}
                  </Typography>

                  {step.fields.map((field) => {
                    if (!isFieldVisible(field)) return null;

                    return (
                      <Box
                        key={field.id}
                        sx={{
                          mb: 3,
                          position: "relative",
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <Box sx={{ flex: 1, "& > div": { width: "100%" } }}>
                          {renderField(field)}
                        </Box>
                        <Box>
                          <FieldInfo
                            infoText={field.tooltip}
                            crmKey={field.crmKey}
                          />
                        </Box>
                      </Box>
                    );
                  })}

                  <div className="separatorEmptyLarge"></div>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Button
                      type={"button"}
                      onClick={handleNextStepClick}
                      variant="contained"
                      fullWidth
                      sx={{
                        borderRadius: 20,
                        backgroundColor:
                          step?.buttons?.next?.bgColor || "#011e37",
                        color: step?.buttons?.next?.color || "#fff",
                        "&:hover": {
                          backgroundColor:
                            step?.buttons?.next?.bgColor || "#011e37",
                          color: step?.buttons?.next?.color || "#fff",
                        },
                      }}
                    >
                      {step?.buttons?.next?.label || "Submit"}
                    </Button>
                  </Box>
                </div>
              );
            })}
          </form>
        </FormProvider>
      </Box>
    </Container>
  );
};

export default memo(SurveyFormRender);
